import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  loginClient,
  loginLawyer,
  refreshUserToken,
  registerClient,
  registerLawyer,
} from '../../services/api';
import {
  getCurrentUser,
  isSessionExpired,
  clearSession,
  setSessionExpiration,
  incrementLoginAttempts,
  clearLoginAttempts,
  isLoginBlocked,
} from '../../utils/authUtils';
import {
  logFailedLogin,
  logSuccessfulLogin,
  logLogout,
  logSessionExpired,
  logRateLimitHit,
  logAuthError,
} from '../../utils/securityLogger';

// Cargar datos persistidos de forma segura
const loadPersistedState = () => {
  try {
    const user = getCurrentUser();
    const token = localStorage.getItem('token');

    // Si la sesión expiró, limpiar datos
    if (user && isSessionExpired()) {
      clearSession();
      return { user: null, token: null, userType: null };
    }

    return {
      user,
      token: token || user?.logg_express || null,
      userType: user?.type || null,
    };
  } catch (error) {
    clearSession();
    return { user: null, token: null, userType: null };
  }
};

const persistedState = loadPersistedState();

// Estados de la autenticación
const INITIAL_STATE = {
  user: persistedState.user,
  token: persistedState.token,
  refreshToken: localStorage.getItem('refreshToken') || null,
  userType: persistedState.userType, // 'client' | 'lawyer'
  currentMode: 'client', // Modo actual en las páginas de auth: 'client' | 'lawyer'
  isAuthenticated: Boolean(persistedState.user && persistedState.token && !isSessionExpired()),
  isLoading: false,
  error: null,
  loginAttempts: 0,
  lastLoginAttempt: null,
  sessionExpiration: localStorage.getItem('sessionExpiration') || null,
  lastActivity: new Date().toISOString(),
};

// Async thunks para operaciones asíncronas
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password, userType }, { rejectWithValue }) => {
    try {
      // Verificar rate limiting
      if (isLoginBlocked()) {
        logRateLimitHit(localStorage.getItem('loginAttempts') || 0);
        return rejectWithValue('Demasiados intentos fallidos. Intente más tarde.');
      }

      const response =
        userType === 'lawyer'
          ? await loginLawyer(email.trim(), password)
          : await loginClient(email.trim(), password);

      if (response.status === 0 || response.status === -3) {
        // Login exitoso - limpiar intentos
        clearLoginAttempts();
        logSuccessfulLogin(userType);

        // Configurar expiración de sesión
        setSessionExpiration(24);

        const userData = {
          id: response.id,
          name: response.name,
          email: email.trim(),
          type: userType,
          logg_express: response.logg_express,
          loginTime: new Date().toISOString(),
        };

        // Guardar en localStorage
        localStorage.setItem('user', JSON.stringify(userData));

        return {
          user: userData,
          userType,
          token: response.token || response.logg_express,
          refreshToken: response.refreshToken || null,
        };
      } else {
        // Login fallido - incrementar intentos
        incrementLoginAttempts();
        logFailedLogin(email, response.message || 'Credenciales incorrectas');
        return rejectWithValue(response.message || 'Credenciales incorrectas');
      }
    } catch (error) {
      // Incrementar intentos en caso de error
      incrementLoginAttempts();
      logAuthError(error, 'loginUser');
      return rejectWithValue(error.message || 'Error de conexión');
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();

      if (!auth.refreshToken && !auth.token) {
        throw new Error('No hay tokens disponibles');
      }

      // Intentar refresh token primero, si no usar el token actual
      const tokenToRefresh = auth.refreshToken || auth.token;
      const response = await refreshUserToken(tokenToRefresh);

      if (response.status === 0) {
        // Actualizar expiración de sesión
        setSessionExpiration(24);

        return {
          token: response.token || response.accessToken,
          refreshToken: response.refreshToken || auth.refreshToken,
          expiresIn: response.expiresIn || 24 * 60 * 60, // 24 horas por defecto
        };
      } else {
        throw new Error(response.message || 'Error al renovar token');
      }
    } catch (error) {
      // Si falla el refresh, cerrar sesión
      clearSession();
      return rejectWithValue('Sesión expirada');
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { getState, dispatch }) => {
  try {
    const { auth } = getState();

    // Intentar notificar al servidor del logout (opcional)
    if (auth.token) {
      // await logoutFromServer(auth.token);
    }
  } catch (error) {
    console.warn('Error al notificar logout al servidor:', error);
  } finally {
    // Siempre limpiar datos locales
    clearSession();

    // Limpiar otros slices si es necesario
    dispatch({ type: 'forms/clearAllForms' });
    dispatch({ type: 'data/clearCache' });
  }

  return null;
});

// Nuevo thunk para verificar y renovar sesión automáticamente
export const verifyAndRefreshSession = createAsyncThunk(
  'auth/verifyAndRefreshSession',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();

      if (!auth.isAuthenticated) {
        return { shouldLogout: false };
      }

      // Verificar si la sesión ha expirado
      if (isSessionExpired()) {
        // Si expiró, solo marcar para logout sin dispatches adicionales
        return rejectWithValue('Session expired');
      }

      return { valid: true };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Registration async thunks
export const registerClientUser = createAsyncThunk(
  'auth/registerClientUser',
  async (clientData, { rejectWithValue }) => {
    try {
      const response = await registerClient(clientData);

      if (response.status === 0) {
        // Registration successful
        const userData = {
          id: response.id || 1,
          name: `${clientData.nombre} ${clientData.apellidopaterno}`,
          email: clientData.correo,
          type: 'client',
          loginTime: new Date().toISOString(),
        };

        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        setSessionExpiration(24);

        return {
          user: userData,
          userType: 'client',
          token: response.token || response.logg_express || null,
          refreshToken: response.refreshToken || null,
        };
      } else {
        return rejectWithValue(response.message || 'Error en el registro');
      }
    } catch (error) {
      console.error('Registration error:', error);
      return rejectWithValue(error.message || 'Error en el registro');
    }
  }
);

export const registerLawyerUser = createAsyncThunk(
  'auth/registerLawyerUser',
  async (lawyerData, { rejectWithValue }) => {
    try {
      const response = await registerLawyer(lawyerData);

      if (response.status === 0) {
        // Registration successful
        const userData = {
          id: response.id || 1,
          name: `${lawyerData.main.lawyer_name} ${lawyerData.main.lawyer_appaterno}`,
          email: lawyerData.main.lawyer_email,
          type: 'lawyer',
          loginTime: new Date().toISOString(),
        };

        // Store in localStorage
        localStorage.setItem('user', JSON.stringify(userData));
        setSessionExpiration(24);

        return {
          user: userData,
          userType: 'lawyer',
          token: response.token || response.logg_express || null,
          refreshToken: response.refreshToken || null,
        };
      } else if (response.status === -2) {
        return rejectWithValue('El RFC ya existe en la base de datos');
      } else {
        return rejectWithValue(response.message || 'Error en el registro');
      }
    } catch (error) {
      console.error('Registration error:', error);
      return rejectWithValue(error.message || 'Error en el registro');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: INITIAL_STATE,
  reducers: {
    // Acciones síncronas
    clearError: state => {
      state.error = null;
    },

    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },

    updateUserProfile: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },

    resetLoginAttempts: state => {
      state.loginAttempts = 0;
      state.lastLoginAttempt = null;
    },

    setCurrentMode: (state, action) => {
      state.currentMode = action.payload; // 'client' | 'lawyer'
    },

    // Acción para restaurar estado desde localStorage (hydration)
    restoreAuth: (state, action) => {
      const userData = action.payload;
      if (userData) {
        state.user = userData;
        state.userType = userData.type;
        state.isAuthenticated = true;
        state.token = userData.logg_express;
      }
    },
  },

  extraReducers: builder => {
    // Login User
    builder
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.userType = action.payload.userType;
        state.token = action.payload.token;
        state.isAuthenticated = true;

        // Guardar datos de usuario y token en localStorage
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
        state.loginAttempts += 1;
        state.lastLoginAttempt = new Date().toISOString();
      })

      // Register Client User
      .addCase(registerClientUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerClientUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.userType = action.payload.userType;
        state.token = action.payload.token;
        state.isAuthenticated = true;

        // Guardar datos de usuario y token en localStorage
        if (action.payload.token) {
          localStorage.setItem('token', action.payload.token);
        }
        if (action.payload.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
      })
      .addCase(registerClientUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Register Lawyer User
      .addCase(registerLawyerUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(registerLawyerUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.userType = action.payload.userType;
        state.token = action.payload.token;
        state.isAuthenticated = true;

        // Guardar datos de usuario y token en localStorage
        if (action.payload.token) {
          localStorage.setItem('token', action.payload.token);
        }
        if (action.payload.user) {
          localStorage.setItem('user', JSON.stringify(action.payload.user));
        }
      })
      .addCase(registerLawyerUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // Logout User
      .addCase(logoutUser.fulfilled, state => {
        // Log del logout
        logLogout('manual');

        // Limpiar persistencia
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return INITIAL_STATE;
      })

      // Verify and Refresh Session
      .addCase(verifyAndRefreshSession.rejected, (state, action) => {
        // Si falla la verificación, limpiar sesión
        if (action.payload === 'Session expired') {
          logSessionExpired();
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          return INITIAL_STATE;
        }
      });
  },
});

// Export actions
export const {
  clearError,
  setUser,
  updateUserProfile,
  resetLoginAttempts,
  restoreAuth,
  setCurrentMode,
} = authSlice.actions;

// Selectors
export const selectAuth = state => state.auth;
export const selectUser = state => state.auth.user;
export const selectIsAuthenticated = state => state.auth.isAuthenticated;
export const selectUserType = state => state.auth.userType;
export const selectCurrentMode = state => state.auth.currentMode;
export const selectIsLawyer = state => state.auth.currentMode === 'lawyer';
export const selectIsLoading = state => state.auth.isLoading;
export const selectAuthError = state => state.auth.error;
export const selectCanAttemptLogin = state => {
  const { loginAttempts, lastLoginAttempt } = state.auth;

  // Permitir máximo 5 intentos
  if (loginAttempts >= 5) {
    // Si han pasado más de 15 minutos, permitir intentar de nuevo
    if (lastLoginAttempt) {
      const timeDiff = new Date() - new Date(lastLoginAttempt);
      return timeDiff > 15 * 60 * 1000; // 15 minutos
    }
    return false;
  }
  return true;
};

export default authSlice.reducer;

// En App.js o componente raíz se dispararía:
// if (INITIAL_STATE.token) {
//   store.dispatch(refreshToken());
// }
